import _template from 'lodash/template';
import api from 'libs/api';
import { master as masterUrl } from './endpoint';

// Simple in-memory cache for static master data
const masterCache = {
  religion: null,
  blood_type: null,
  marital_status: null,
  gender: null,
};

// Default TTL for cache entries (in milliseconds)
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const isCacheValid = (entry, ttl = DEFAULT_CACHE_TTL_MS) => {
  if (!entry || !entry.timestamp) return false;
  const now = Date.now();
  return now - entry.timestamp < ttl;
};

export const clearMasterCache = () => {
  Object.keys(masterCache).forEach((k) => {
    masterCache[k] = null;
  });
};

/** religion */
export const getReligion = async (config = {}) => {
  // config may include ttl or force flags
  const ttl = config.ttl ?? DEFAULT_CACHE_TTL_MS;
  const forceRefresh = config.force ?? false;
  const cacheKey = JSON.stringify(config || {});
  if (!forceRefresh && masterCache.religion && masterCache.religion._cacheKey === cacheKey && isCacheValid(masterCache.religion, ttl)) {
    return masterCache.religion.resp;
  }
  const resp = await api().get(masterUrl.religion, config);
  masterCache.religion = { _cacheKey: cacheKey, resp, timestamp: Date.now() };
  return resp;
};

/** Job position */
export const getJobPosition = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.jobPosition, { params, ...config });
  return resp;
};

export const addJobPosition = async (data, config = {}) => {
  const resp = await api().post(masterUrl.jobPosition, data, config);
  return resp;
};

export const updateJobPosition = async (id, data, config = {}) => {
  const url = _template(masterUrl.jobPositionId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteJobPosition = async (id, config = {}) => {
  const url = _template(masterUrl.jobPositionId)({ id }) + '/delete';
  const resp = await api().post(url, config);
  return resp;
};

/** job status */
export const getJobStatus = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.jobStatus, { params, ...config });
  return resp;
};

export const addJobStatus = async (data, config = {}) => {
  const resp = await api().post(masterUrl.jobStatus, data, config);
  return resp;
};

export const jobStatusSequenceUp = async (id, config = {}) => {
  const url = _template(masterUrl.jobStatusSequenceUp)({ id });
  const resp = await api().put(url);
  return resp;
};

export const jobStatusSequenceDown = async (id, config = {}) => {
  const url = _template(masterUrl.jobStatusSequenceDown)({ id });
  const resp = await api().put(url);
  return resp;
};

export const updateJobStatus = async (id, data, config = {}) => {
  const url = _template(masterUrl.jobStatusId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteJobStatus = async (id, config = {}) => {
  const url = _template(masterUrl.jobStatusId)({ id }) + '/delete';
  const resp = await api().post(url, config);
  return resp;
};

/** Job Level */
export const getJobLevel = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.jobLevel, { params, ...config });
  return resp;
};
/** school maste4 */
export const getSchool = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.school, { params, ...config });
  return resp;
};

export const addSchool = async (data, config = {}) => {
  const resp = await api().post(masterUrl.school, data, config);
  return resp;
};

export const schoolSequenceUp = async (id, config = {}) => {
  const url = _template(masterUrl.schoolSequenceUp)({ id });
  const resp = await api().put(url);
  return resp;
};

export const schoolSequenceDown = async (id, config = {}) => {
  const url = _template(masterUrl.schoolSequenceDown)({ id });
  const resp = await api().put(url);
  return resp;
};

export const updateSchool = async (id, data, config = {}) => {
  const url = _template(masterUrl.schoolId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteSchool = async (id, config = {}) => {
  const url = _template(masterUrl.schoolId)({ id }) + '/delete';
  const resp = await api().post(url, config);
  return resp;
};

/** school location master */
export const getLocation = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.location, { params, ...config });
  return resp;
};

export const addLocation = async (data, config = {}) => {
  const resp = await api().post(masterUrl.location, data, config);
  return resp;
};

export const updateLocation = async (id, data, config = {}) => {
  const url = _template(masterUrl.locationId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteLocation = async (id, config = {}) => {
  const url = _template(masterUrl.locationId)({ id }) + '/delete';
  const resp = await api().post(url, config);
  return resp;
};

/** skill master */
export const getSkill = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.skill, { params, ...config });
  return resp;
};

export const addSkill = async (data, config = {}) => {
  const resp = await api().post(masterUrl.skill, data, config);
  return resp;
};

export const updateSkill = async (id, data, config = {}) => {
  const url = _template(masterUrl.skillId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteSkill = async (id, config = {}) => {
  const url = _template(masterUrl.skillId)({ id }) + '/delete';
  const resp = await api().post(url, config);
  return resp;
};

/** blood type master */
export const getBloodType = async (params = {}, config = {}) => {
  const ttl = config.ttl ?? DEFAULT_CACHE_TTL_MS;
  const forceRefresh = config.force ?? false;
  const cacheKey = JSON.stringify({ params, ...config } || {});
  if (!forceRefresh && masterCache.blood_type && masterCache.blood_type._cacheKey === cacheKey && isCacheValid(masterCache.blood_type, ttl)) {
    return masterCache.blood_type.resp;
  }
  const resp = await api().get(masterUrl.blood_type, { params, ...config });
  masterCache.blood_type = { _cacheKey: cacheKey, resp, timestamp: Date.now() };
  return resp;
};

/** marital master */
export const getMaritalStatus = async (params = {}, config = {}) => {
  const ttl = config.ttl ?? DEFAULT_CACHE_TTL_MS;
  const forceRefresh = config.force ?? false;
  const cacheKey = JSON.stringify({ params, ...config } || {});
  if (!forceRefresh && masterCache.marital_status && masterCache.marital_status._cacheKey === cacheKey && isCacheValid(masterCache.marital_status, ttl)) {
    return masterCache.marital_status.resp;
  }
  const resp = await api().get(masterUrl.marital_status, { params, ...config });
  masterCache.marital_status = { _cacheKey: cacheKey, resp, timestamp: Date.now() };
  return resp;
};

/** gender master */
export const getGender = async (params = {}, config = {}) => {
  const ttl = config.ttl ?? DEFAULT_CACHE_TTL_MS;
  const forceRefresh = config.force ?? false;
  const cacheKey = JSON.stringify({ params, ...config } || {});
  if (!forceRefresh && masterCache.gender && masterCache.gender._cacheKey === cacheKey && isCacheValid(masterCache.gender, ttl)) {
    return masterCache.gender.resp;
  }
  const resp = await api().get(masterUrl.gender, { params, ...config });
  masterCache.gender = { _cacheKey: cacheKey, resp, timestamp: Date.now() };
  return resp;
};

/** Division */
export const getDivision = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.division, { params, ...config });
  return resp;
};

/* Position */
export const getPosition = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.position, { params, ...config });
  return resp;
};

/** Employee Status */
export const getEmployeeStatus = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.employeeStatus, { params, ...config });
  return resp;
};

/** Interview Forms */
export const getInterviewForms = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.interviewForms, { params, ...config });
  return resp;
};

export const getInterviewForm = async (id, config = {}) => {
  const url = _template(masterUrl.interviewFormsId)({ id });
  const resp = await api().get(url, config);
  return resp;
};

export const addInterviewForm = async (data, config = {}) => {
  const resp = await api().post(masterUrl.interviewForms, data, config);
  return resp;
};

export const updateInterviewForm = async (id, data, config = {}) => {
  const url = _template(masterUrl.interviewFormsId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteInterviewForm = async (id, config = {}) => {
  const url = _template(masterUrl.interviewFormsId)({ id });
  const resp = await api().delete(url, config);
  return resp;
};

export const getInterviewFormSurveyJs = async (id, config = {}) => {
  const url = _template(masterUrl.interviewFormsSurveyJs)({ id });
  const resp = await api().get(url, config);
  return resp;
};

// Interview form answers endpoints
export const getInterviewFormAnswers = async (params = {}, config = {}) => {
  const resp = await api().get('/interview_forms/answers', { params, ...config });
  return resp;
};

export const saveInterviewFormAnswers = async (data, config = {}) => {
  const resp = await api().post('/interview_forms/answers', data, config);
  return resp;
};

/** Offering Letter Forms */
export const getOfferingLetterForms = async (params = {}, config = {}) => {
  const resp = await api().get(masterUrl.offeringLetterForms, { params, ...config });
  return resp;
};

export const getOfferingLetterForm = async (id, config = {}) => {
  const url = _template(masterUrl.offeringLetterFormsId)({ id });
  const resp = await api().get(url, config);
  return resp;
};

export const addOfferingLetterForm = async (data, config = {}) => {
  const resp = await api().post(masterUrl.offeringLetterForms, data, config);
  return resp;
};

export const updateOfferingLetterForm = async (id, data, config = {}) => {
  const url = _template(masterUrl.offeringLetterFormsId)({ id });
  const resp = await api().put(url, data, config);
  return resp;
};

export const deleteOfferingLetterForm = async (id, config = {}) => {
  const url = _template(masterUrl.offeringLetterFormsId)({ id });
  const resp = await api().delete(url, config);
  return resp;
};

export const getOfferingLetterFormSurveyJs = async (id, config = {}) => {
  const url = _template(masterUrl.offeringLetterFormsSurveyJs)({ id });
  const resp = await api().get(url, config);
  return resp;
};

export const getOfferingLetterFormVariables = async (config = {}) => {
  const resp = await api().get(masterUrl.offeringLetterFormsVariables, config);
  return resp;
};
